/**
 * GTIN checks, the same rule as uc-core (src/modules/pack/utils/qr-link.util.ts): 8, 12, 13 or 14
 * digits whose last digit is the GS1 mod-10 check digit; stored left-padded to 14.
 */

export const GTIN_LENGTHS = [8, 12, 13, 14] as const

/** The GS1 check digit for the digits before it (weights 3,1,3,… from the right). */
export function gs1CheckDigit(body: string): number | null {
  if (!/^\d+$/.test(body)) return null
  let sum = 0
  for (let i = 0; i < body.length; i += 1) {
    const d = Number(body[body.length - 1 - i])
    sum += i % 2 === 0 ? d * 3 : d
  }
  return (10 - (sum % 10)) % 10
}

/** Last digit matches the check digit of the rest. */
export function gs1CheckDigitOk(digits: string): boolean {
  if (!/^\d+$/.test(digits) || digits.length < 2) return false
  return gs1CheckDigit(digits.slice(0, -1)) === Number(digits[digits.length - 1])
}

/** Digits only, spaces and dashes dropped (people paste "890 1234 56789 1"). */
export const cleanGtin = (raw: string) => raw.replace(/[\s-]/g, '')

/** What is wrong with a typed GTIN, or null when it is fine (or empty — it is optional). */
export function gtinProblem(raw: string): string | null {
  const g = cleanGtin(raw)
  if (!g) return null
  if (!/^\d+$/.test(g)) return 'Digits only'
  if (!(GTIN_LENGTHS as readonly number[]).includes(g.length)) return '8, 12, 13 or 14 digits'
  if (!gs1CheckDigitOk(g)) {
    const want = gs1CheckDigit(g.slice(0, -1))
    return `The last digit should be ${want} — check the number`
  }
  return null
}

/** A valid GTIN left-padded to 14 digits; null when invalid. */
export function normaliseGtin(raw: string | null | undefined): string | null {
  const g = cleanGtin(raw ?? '')
  if (!g || gtinProblem(g)) return null
  return g.padStart(14, '0')
}
